import { useState } from 'react'
import { Button, Input, Dropdown } from 'components'
import Flex from 'components/priority/Flex/Flex'
import Text from 'components/priority/Text/Text'
import { Tooltip } from '@/components/global/Tooltip/Tooltip'
import { editorPagesAtom, isSavingAtom } from '@/atoms/_editorAtoms'
import {
  editorLayoutAtom,
  themeOverrideAtom,
  themeIdAtom,
} from 'atoms/editorAtoms'
import { applicationTypeAtom } from 'atoms'
import { useRecoilValue } from 'recoil'
import { useSetToast } from 'hooks'
import { ToggleSwitch } from 'components/global/ToggleSwitch/ToggleSwitch'
import { useErrorHandler } from 'hooks/utility/useErrorHandler'
import { templatePageBodies } from '@/utils/editor/templatePageBodies'
import { useEditorLayoutSaveRefusal } from '@/hooks/editor/useEditorLayoutSaveRefusal'
import { LOCALE, LOCALE_OPTIONS } from '@messenger/consts'
import { useTranslation } from 'react-i18next'
import { createWithPages } from '@/api/pageSets'
import { useDropEditorTextBoxWork } from '@/hooks/editor/useDropEditorTextBoxWork'
import { useEditorSaveTextBoxes } from '@/hooks/editor/useEditorSaveTextBoxes'
import { useEditorSaveGesture } from '@/hooks/editor/useEditorSaveGesture'

export const CreateLayoutDrawer = ({ basePages, setShow }) => {
  const { t } = useTranslation()
  const handleError = useErrorHandler('CreateLayoutDrawer')
  const [layoutIsDefault, setLayoutIsDefault] = useState(false)
  const [layoutName, setLayoutName] = useState(null)
  const [layoutLanguage, setLayoutLanguage] = useState(LOCALE.EN)
  const [savingLayout, setSavingLayout] = useState(false)
  const editorPages = useRecoilValue(editorPagesAtom)
  const layout = useRecoilValue(editorLayoutAtom)
  const themeOverride = useRecoilValue(themeOverrideAtom)
  const themeId = useRecoilValue(themeIdAtom)
  const applicationType = useRecoilValue(applicationTypeAtom)
  const theme = themeOverride || themeId
  const setToast = useSetToast()
  const saveEditorTextBoxes = useEditorSaveTextBoxes()
  const runSaveGesture = useEditorSaveGesture()
  const dropTextBoxWorkForPages = useDropEditorTextBoxWork()
  const isSaving = useRecoilValue(isSavingAtom)
  const showSaveRefusal = useEditorLayoutSaveRefusal(
    'CreateLayoutDrawer',
    setShow,
  )

  /**
   * Creates the layout with its pages in one request, which creates both or
   * nothing, filed by the server under the saving user's funeral home (or,
   * for Lotus pages, a listed order's), and says why when it is refused.
   */
  const createPageSet = async () => {
    const themeExistsAndIsObject = theme && typeof theme === 'object'
    const themeId = themeExistsAndIsObject ? theme.theme_id : theme
    try {
      await createWithPages({
        name: layoutName,
        productId: layout, // this references the product id
        themeId,
        default: layoutIsDefault,
        language: layoutLanguage,
        pages: templatePageBodies(basePages, editorPages),
      })
      setToast({ text: t('toast.layoutSavedSuccess') })
    } catch (error) {
      showSaveRefusal(error)
    }
  }

  const createSavedLayout = async () => {
    try {
      setSavingLayout(true)
      // A layout is a copy of the page rows, so unsaved text-box edits have
      // to land first — a copy taken over stale rows would bake the stale
      // words into the template. That write is a Save of the line, so it runs
      // as one: under the tab's save latch, after a version of the line. The
      // copy below writes a template rather than the line, so it runs outside
      // the latch and a slow copy never stops the editor saving.
      const savedBoxes = await runSaveGesture(
        'create_layout',
        applicationType,
        () => saveEditorTextBoxes(),
      )
      // Another Save is writing the line; its own gesture says so.
      if (savedBoxes === undefined) return
      if (savedBoxes.stale) {
        // The page this layout would copy is no longer on the line, so the
        // copy has nothing to take. Its drafts go with it, and the message
        // names what happened instead of blaming the save.
        dropTextBoxWorkForPages(savedBoxes.stalePageIds)
        setToast({ text: t('toast.editorPagesReplaced') })
        return
      }
      // This tab's link no longer reaches the order; the editor's access-lost
      // prompt already says the save did not complete.
      if (savedBoxes.accessLost) return
      if (!savedBoxes.saved) {
        setToast({ text: t('toast.layoutTextBoxSaveFailed') })
        return
      }
      await createPageSet()
    } catch (error) {
      handleError(error?.response?.data?.message || error.message, error)
    } finally {
      setSavingLayout(false)
    }
  }

  return (
    <Flex margin="16px 24px" column>
      <Flex column margin="24px 0">
        <Text>
          {t('editor.menus.assets.savedLayouts.saveProgressDescription')}
        </Text>
      </Flex>
      <Flex
        justify="space-between"
        align="center"
        padding="16px 0 8px"
        borderTop="1px solid #B0B6BC"
      >
        <Flex column gap="16px">
          <Tooltip
            text={t('editor.menus.assets.savedLayouts.changeLayoutName')}
            margin="0 12px 0 0"
          >
            <Input
              label={t('editor.menus.assets.savedLayouts.layoutName')}
              placeholder={t(
                'editor.menus.assets.savedLayouts.layoutNamePlaceholder',
              )}
              value={[layoutName, setLayoutName]}
            />
          </Tooltip>
          <Tooltip
            text={t('editor.menus.assets.savedLayouts.selectLayoutLanguage')}
            margin="0 12px 0 0"
          >
            <Dropdown
              label={t('editor.menus.assets.savedLayouts.languageLabel')}
              selected={[layoutLanguage, setLayoutLanguage]}
              items={LOCALE_OPTIONS.map((opt) => ({
                ...opt,
                label: t(`editor.language.${opt.label}`),
              }))}
              textKey="label"
              valueKey="value"
            />
          </Tooltip>
        </Flex>
        <Tooltip
          text={t('editor.menus.assets.savedLayouts.makeLayoutDefault')}
          margin="0 12px 0 0"
        >
          <ToggleSwitch
            label={t('editor.menus.assets.savedLayouts.defaultLabel')}
            value={[layoutIsDefault, setLayoutIsDefault]}
            margin="0 16px"
          />
        </Tooltip>
      </Flex>
      <Flex justify="right" align="center" margin="24px 16px">
        <Button disabled={savingLayout || isSaving} onClick={createSavedLayout}>
          {savingLayout
            ? t('editor.menus.assets.savedLayouts.savingLayout')
            : t('editor.menus.assets.savedLayouts.createLayoutButton')}
        </Button>
      </Flex>
    </Flex>
  )
}

export default CreateLayoutDrawer
